"use client";

import { useState, useCallback } from "react";
import Image from "next/image";
import Link from "next/link";
import MagneticButton from "@/components/MagneticButton";
import AmbientCanvas from "@/components/AmbientCanvas";
import BottleVisual from "@/components/BottleVisual";
import Preloader from "@/components/Preloader";
import SmoothScroll from "@/components/SmoothScroll";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import ScrollAnimations from "@/components/ScrollAnimations";
import RevealObserver from "@/components/RevealObserver";

interface Category {
  id: string;
  name: string;
  subtitle: string;
  description: string;
  image: string;
  accent: string;
  productCount: number;
  products: { id: string; name: string; price: string; image: string }[];
  href: string;
}

const categories: Category[] = [
  {
    id: "pour-femme",
    name: "Pour Femme",
    subtitle: "Feminine Signature",
    description: "Night-blooming florals wrapped in incense and dark amber — worn in candlelight, remembered at dawn.",
    image: "/images/pink_bottle.webp",
    accent: "#c47a8b",
    productCount: 1,
    products: [
      { id: "belladonna", name: "Belladonna", price: "PKR 2500", image: "/images/pink_bottle.webp" }
    ],
    href: "/#collection"
  },
  {
    id: "pour-homme",
    name: "Pour Homme",
    subtitle: "Masculine Signature",
    description: "Smoke, earth, and sinew. A leather-and-oud noir that never announces itself — it simply never leaves.",
    image: "/images/blue_bottle.webp",
    accent: "#5b7d9b",
    productCount: 1,
    products: [
      { id: "wolfsbane", name: "Wolfsbane", price: "PKR 2500", image: "/images/blue_bottle.webp" }
    ],
    href: "/#collection"
  },
  {
    id: "the-vault",
    name: "The Vault",
    subtitle: "Limited Editions",
    description: "Rare formulations aged in darkness. Numbered batches sealed in hand-finished crystal — for the few who seek the exceptional.",
    image: "/images/pair_banner.webp",
    accent: "#c9a84c",
    productCount: 2,
    products: [
      { id: "belladonna", name: "Belladonna", price: "PKR 2500", image: "/images/pink_bottle.webp" },
      { id: "wolfsbane", name: "Wolfsbane", price: "PKR 2500", image: "/images/blue_bottle.webp" }
    ],
    href: "/#vault"
  },
  {
    id: "alchemy",
    name: "Alchemy",
    subtitle: "Raw Ingredients",
    description: "The soul of each scent. Sourced from volcanic highlands, moon-harvested valleys, and ancient forests — presented in their purest form.",
    image: "/images/banner-1.webp",
    accent: "#8a6a2c",
    productCount: 12,
    products: [],
    href: "/#alchemy"
  }
];

function CategoryCard({ category, index }: { category: Category; index: number }) {
  const isLast = index === categories.length - 1;
  const hasProducts = category.products.length > 0;

  return (
    <article
      className="category-card relative group overflow-hidden"
      style={{ transitionDelay: `${index * 0.1}s` }}
    >
      {/* Ambient glow */}
      <AmbientCanvas
        variant="mist"
        accent={category.accent}
        density={0.3}
        className="absolute inset-0 opacity-30 mix-blend-multiply pointer-events-none"
        aria-hidden="true"
      />

      {/* Orb glow - responsive sizing */}
      <div
        className="orb-glow -bottom-1/4 -right-1/4 w-[22rem] h-[22rem] md:w-[28rem] md:h-[28rem] lg:w-[32rem] lg:h-[32rem] opacity-50 pointer-events-none"
        style={{ background: `radial-gradient(circle, ${category.accent}1a 0%, transparent 65%)` }}
        aria-hidden="true"
      />

      <Link
        href={category.href}
        className="block relative h-full min-h-[480px] md:min-h-[560px] lg:min-h-[600px] gold-frame gold-glow-hover reveal-blur p-6 md:p-8 lg:p-10 flex flex-col"
        style={{ transitionDelay: `${index * 0.1}s` }}
      >
        <div className="relative flex flex-col h-full">
          {/* Top: Category badge + count */}
          <div className="flex items-start justify-between mb-6 md:mb-8">
            <div className="flex items-center gap-3">
              <div className="w-6 h-px md:w-8" style={{ backgroundColor: category.accent }} />
              <span className="text-[8px] md:text-[9px] tracking-[0.3em] uppercase" style={{ color: category.accent }}>
                {category.subtitle}
              </span>
            </div>
            <span className="font-mono text-[8px] md:text-[9px] tracking-[0.2em] uppercase text-[#6f6759]/50">
              {category.productCount} {category.productCount === 1 ? "Scent" : "Scents"}
            </span>
          </div>

          {/* Center: Visual showcase - takes remaining space */}
          <div className="relative flex-1 flex items-center justify-center my-6 md:my-8 min-h-0">
            {hasProducts ? (
              <div className="relative flex items-center justify-center gap-4 md:gap-8 lg:gap-12 w-full">
                {category.products.map((product, i) => (
                  <div key={product.id} className="relative flex-shrink-0" style={{ transitionDelay: `${i * 0.1}s` }}>
                    <BottleVisual
                      src={product.image}
                      alt={`${product.name} bottle`}
                      name={product.name}
                      glow={category.accent}
                      showNameplate={false}
                      priority
                      sizes="(max-width: 640px) 90vw, (max-width: 1024px) 40vw, 22vw"
                    />
                    {/* Silk backdrop per bottle - responsive */}
                    <div
                      className="silk left-[-8%] top-[6%] w-[16rem] h-[12rem] md:w-[20rem] md:h-[16rem] lg:w-[24rem] lg:h-[18rem] opacity-40 pointer-events-none"
                      style={{
                        background: `radial-gradient(ellipse at center, ${category.accent}2e, ${category.accent}08 55%, transparent 75%)`,
                        animationDelay: `${-8 - i * 4}s`
                      }}
                      aria-hidden="true"
                    />
                  </div>
                ))}
              </div>
            ) : (
              <div className="relative w-full max-w-lg mx-auto">
                <div
                  className="aspect-[4/5] relative overflow-hidden rounded-[2px] bg-gradient-to-br"
                  style={{
                    background: `linear-gradient(135deg, ${category.accent}15 0%, ${category.accent}05 100%)`
                  }}
                >
                  <Image
                    src={category.image}
                    alt={category.name}
                    fill
                    className="object-cover"
                    sizes="(max-width: 640px) 90vw, (max-width: 1024px) 50vw, 30vw"
                    priority={index === 0}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/10 via-transparent to-transparent" />
                </div>
                <div className="silk left-[-10%] top-[4%] w-[22rem] h-[16rem] md:w-[28rem] md:h-[20rem] opacity-35 pointer-events-none"
                  style={{
                    background: `radial-gradient(ellipse at center, ${category.accent}2e, ${category.accent}08 55%, transparent 75%)`
                  }}
                  aria-hidden="true"
                />
              </div>
            )}
          </div>

          {/* Bottom: Details */}
          <div className="border-t border-[#221d17]/10 pt-6 md:pt-8 mt-auto">
            <h3 className="font-serif text-2xl md:text-3xl lg:text-4xl text-[#221d17] mb-3" style={{ fontWeight: 300, lineHeight: 1.1 }}>
              {category.name}
            </h3>
            <p className="text-xs md:text-sm text-[#6f6759]/70 italic mb-4 md:mb-5 max-w-md leading-relaxed">{category.description}</p>

            {hasProducts && (
              <div className="flex flex-wrap gap-2 md:gap-3 mb-4 md:mb-6">
                {category.products.map((product) => (
                  <span
                    key={product.id}
                    className="px-2.5 md:px-3 py-1 md:py-1.5 text-[8px] md:text-[9px] tracking-[0.1em] uppercase font-mono bg-[#faf6ef] border border-[#221d17]/10 text-[#6f6759]/70 hover:border-[#c9a84c]/40 hover:text-[#8a6a2c] transition-all duration-300 whitespace-nowrap"
                  >
                    {product.name} · {product.price}
                  </span>
                ))}
              </div>
            )}

            <MagneticButton strength={0.15}>
              <button className="btn-elegant w-full sm:w-auto" data-cursor="pointer">
                <span>Explore {category.name}</span>
              </button>
            </MagneticButton>
          </div>
        </div>
      </Link>

      {/* Divider between cards */}
      {!isLast && (
        <div className="absolute bottom-0 left-6 md:left-12 right-6 md:right-12 h-px" style={{ background: `linear-gradient(90deg, transparent, ${category.accent}40, transparent)` }} />
      )}
    </article>
  );
}

export default function CategoriesPage() {
  const [loaded, setLoaded] = useState(false);
  const [showContent, setShowContent] = useState(false);
  const [orderModalOpen, setOrderModalOpen] = useState(false);
  const [preselectedProduct, setPreselectedProduct] = useState<string | undefined>();

  const handlePreloaderComplete = useCallback(() => {
    setLoaded(true);
    requestAnimationFrame(() => {
      setShowContent(true);
    });
  }, []);

  const openOrderModal = useCallback((product?: string) => {
    setPreselectedProduct(product);
    setOrderModalOpen(true);
  }, []);

  return (
    <>
      {!loaded && <Preloader onComplete={handlePreloaderComplete} />}

      {/* Film-grain texture overlay */}
      <div
        className="bg-noise fixed inset-0 z-[9997] pointer-events-none opacity-[0.04]"
        aria-hidden="true"
      />

      <SmoothScroll>
        <main
          role="main"
          aria-label="STYGIAN Categories"
          className={`transition-[opacity,filter,transform] duration-1000 ${
            showContent ? "opacity-100 blur-0 scale-100" : "opacity-0 page-blur"
          }`}
        >
          <Navigation onOrderClick={() => openOrderModal()} />

          {/* Scroll progress bar */}
          <div
            className="fixed top-0 left-0 right-0 z-[60] h-[2px] pointer-events-none"
            role="progressbar"
            aria-label="Scroll progress"
          >
            <div
              className="scroll-progress-line h-full origin-left"
              style={{
                background: "linear-gradient(90deg, rgba(201,168,76,0.2), #c9a84c, rgba(201,168,76,0.2))",
                transform: "scaleX(0)",
              }}
            />
          </div>

          <div className="relative z-10">
            {/* Hero Section */}
            <section id="hero" aria-label="Categories Hero" className="relative min-h-[60vh] md:min-h-[75vh] lg:min-h-[85vh] flex items-center">
              <div className="absolute inset-0 marble opacity-60" aria-hidden="true" />
              <div className="hairline-grid absolute inset-0 opacity-25" aria-hidden="true" />

              {/* Silk ambience - responsive */}
              <div
                className="silk left-[-6%] top-[10%] w-[32rem] h-[22rem] md:w-[40rem] md:h-[28rem] lg:w-[48rem] lg:h-[32rem]"
                style={{ background: "radial-gradient(ellipse at center, rgba(255,252,245,0.9), rgba(248,240,226,0.5) 60%, transparent 75%)" }}
                aria-hidden="true"
              />
              <div
                className="silk right-[-8%] bottom-[6%] w-[30rem] h-[22rem] md:w-[38rem] md:h-[28rem] lg:w-[44rem] lg:h-[32rem]"
                style={{ background: "radial-gradient(ellipse at center, rgba(216,186,128,0.25), rgba(216,186,128,0.08) 60%, transparent 78%)", animationDelay: "-11s" }}
                aria-hidden="true"
              />

              {/* Sunbeam */}
              <div className="sunbeam left-[-10%]" aria-hidden="true" />

              <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 pt-24 md:pt-32 lg:pt-40">
                <div className="reveal-blur text-center max-w-3xl mx-auto">
                  <p className="text-[9px] md:text-[10px] tracking-[0.4em] uppercase text-[#8a6a2c]/70 mb-4 md:mb-6">
                    Our Categories
                  </p>
                  <h1 className="font-serif text-4xl md:text-6xl lg:text-7xl xl:text-8xl tracking-tight text-[#221d17] mb-4 md:mb-6" style={{ fontWeight: 300, lineHeight: 1.05 }}>
                    Discover by
                    <br />
                    <span className="text-shimmer-gold">Essence</span>
                  </h1>
                  <p className="text-sm md:text-base lg:text-lg text-[#6f6759]/70 font-light max-w-2xl mx-auto leading-relaxed">
                    Each category holds a different shadow. From feminine florals to masculine woods,
                    from the core collection to the locked vault — find the scent that speaks your language.
                  </p>
                </div>

                {/* Scroll indicator */}
                <div className="absolute bottom-8 md:bottom-10 left-1/2 -translate-x-1/2 reveal-blur" style={{ transitionDelay: "0.4s" }}>
                  <div className="flex flex-col items-center gap-3">
                    <div className="w-5 h-5 md:w-6 md:h-6">
                      <svg viewBox="0 0 24 24" fill="none" stroke="#c9a84c" strokeWidth="1.5" className="w-full h-full animate-[scrollDot_2s_ease-in-out_infinite]">
                        <path d="M12 5v14M19 12l-7 7-7-7" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </div>
                    <span className="text-[8px] md:text-[9px] tracking-[0.3em] uppercase text-[#8a6a2c]/60">Explore</span>
                  </div>
                </div>
              </div>
            </section>

            {/* Categories Grid */}
            <section id="categories" aria-label="Fragrance Categories" className="relative py-16 md:py-24 lg:py-32" style={{ background: "linear-gradient(180deg, #f2ecdf 0%, #efe9df 100%)" }}>
              <div className="absolute inset-0 marble opacity-40" aria-hidden="true" />
              <div className="hairline-grid absolute inset-0 opacity-15" aria-hidden="true" />

              <div className="relative max-w-[1400px] mx-auto px-6 md:px-12">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8 lg:gap-10">
                  {categories.map((category, index) => (
                    <CategoryCard key={category.id} category={category} index={index} />
                  ))}
                </div>
              </div>
            </section>

            {/* Featured Scents Quick Access */}
            <section id="featured" aria-label="Featured Scents" className="relative py-16 md:py-24 lg:py-32 overflow-hidden" style={{ background: "#f9f6f0" }}>
              <div className="absolute inset-0 marble opacity-50" aria-hidden="true" />
              <div className="hairline-grid absolute inset-0 opacity-20" aria-hidden="true" />

              <div className="relative max-w-[1400px] mx-auto px-6 md:px-12">
                {/* Header */}
                <div className="mb-12 md:mb-16 lg:mb-24">
                  <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
                    <div>
                      <p className="reveal-blur text-[9px] md:text-[10px] tracking-[0.4em] uppercase text-[#8a6a2c]/70 mb-3 md:mb-4">
                        Quick Access
                      </p>
                      <h2 className="reveal-blur font-serif text-3xl md:text-5xl lg:text-6xl tracking-tight text-[#221d17]" style={{ fontWeight: 300, transitionDelay: "0.1s", lineHeight: 1.1 }}>
                        Featured
                        <br />
                        <span className="text-shimmer-gold">Scents</span>
                      </h2>
                    </div>
                    <p className="reveal-blur text-sm md:text-base text-[#6f6759]/70 max-w-sm font-light" style={{ transitionDelay: "0.2s" }}>
                      Our two signatures. Each aged in darkness, sealed in crystal, released in numbered batches.
                    </p>
                  </div>
                  <div className="section-divider w-12 md:w-16 h-px mt-6 md:mt-8 bg-gradient-to-r from-[#c9a84c]/60 to-transparent" />
                </div>

                {/* Product Cards - Compact */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
                  {[
                    {
                      id: "belladonna",
                      name: "Belladonna",
                      subtitle: "Pour Femme · Eau de Parfum",
                      tagline: "Grace in every drop. Allure that lingers.",
                      image: "/images/pink_bottle.webp",
                      accent: "#c47a8b",
                      price: "PKR 2500",
                      edition: "Batch Nº 001",
                      notes: { top: ["Nightshade", "Black Currant", "Pink Pepper"], heart: ["Tuberose", "Jasmine Sambac", "Incense"], base: ["Dark Amber", "Patchouli", "Sandalwood"] },
                      href: "/#collection"
                    },
                    {
                      id: "wolfsbane",
                      name: "Wolfsbane",
                      subtitle: "Pour Homme · Eau de Parfum",
                      tagline: "Untamed by nature. Unforgettable by choice.",
                      image: "/images/blue_bottle.webp",
                      accent: "#5b7d9b",
                      price: "PKR 2500",
                      edition: "Batch Nº 002",
                      notes: { top: ["Black Pepper", "Bergamot", "Cardamom"], heart: ["Smoked Vetiver", "Dark Iris", "Leather"], base: ["Oud", "Amber", "Musk"] },
                      href: "/#collection"
                    }
                  ].map((product, index) => (
                    <Link
                      key={product.id}
                      id={product.id}
                      href={product.href}
                      className="reveal-blur group relative gold-frame gold-glow-hover p-5 md:p-6 lg:p-8 overflow-hidden"
                      style={{ transitionDelay: `${0.1 + index * 0.1}s` }}
                    >
                      <AmbientCanvas
                        variant="petals"
                        accent={product.accent}
                        density={0.35}
                        className="absolute inset-0 opacity-40 mix-blend-multiply pointer-events-none"
                        aria-hidden="true"
                      />

                      <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8 items-center">
                        {/* Bottle */}
                        <div className={`relative ${product.id === "wolfsbane" ? "lg:order-2" : ""}`}>
                          <div
                            className="silk left-[-6%] top-[4%] w-[16rem] h-[12rem] md:w-[20rem] md:h-[16rem] lg:w-[24rem] lg:h-[18rem] pointer-events-none"
                            style={{
                              background: `radial-gradient(ellipse at center, ${product.accent}2e, ${product.accent}12 55%, transparent 75%)`,
                            }}
                            aria-hidden="true"
                          />
                          <BottleVisual
                            src={product.image}
                            alt={`${product.name} bottle`}
                            name={product.name}
                            glow={product.accent}
                            showNameplate={false}
                            sizes="(max-width: 640px) 90vw, (max-width: 1024px) 50vw, 30vw"
                          />
                        </div>

                        {/* Details */}
                        <div className={`relative ${product.id === "wolfsbane" ? "lg:order-1" : ""}`}>
                          <div className="flex items-center gap-2 md:gap-3 mb-3 md:mb-4">
                            <div className="w-5 md:w-6 h-px" style={{ backgroundColor: product.accent }} />
                            <p className="text-[8px] md:text-[9px] tracking-[0.3em] uppercase" style={{ color: product.accent }}>
                              {product.subtitle}
                            </p>
                          </div>

                          <h3 className="font-serif text-2xl md:text-3xl lg:text-4xl text-[#221d17] mb-2 md:mb-3" style={{ fontWeight: 300, lineHeight: 1.1 }}>
                            {product.name}
                          </h3>

                          <p className="text-xs md:text-sm text-[#6f6759]/70 italic mb-3 md:mb-4">{product.tagline}</p>

                          <div className="flex items-center justify-between mb-4 md:mb-5">
                            <span className="font-mono text-[8px] md:text-[9px] tracking-[0.2em] uppercase text-[#6f6759]/60">
                              {product.edition}
                            </span>
                            <span className="font-mono text-[8px] md:text-[9px] tracking-[0.2em] uppercase" style={{ color: product.accent }}>
                              {product.price}
                            </span>
                          </div>

                          {/* Notes preview */}
                          <div className="border-t border-b border-[#221d17]/8 mb-5 md:mb-6">
                            {["top", "heart", "base"].map((level, i) => (
                              <div key={level} className={`flex items-center justify-between py-2 md:py-2.5 ${i < 2 ? "border-b border-[#221d17]/8" : ""}`}>
                                <span className="text-[6px] md:text-[7px] tracking-[0.3em] uppercase w-10 md:w-12" style={{ color: product.accent }}>
                                  {level.charAt(0).toUpperCase() + level.slice(1)}
                                </span>
                                <span className="text-[7px] md:text-[9px] text-[#6f6759]/70 text-right leading-relaxed">
                                  {product.notes[level as keyof typeof product.notes].join(" · ")}
                                </span>
                              </div>
                            ))}
                          </div>

                          <MagneticButton strength={0.2}>
                            <button className="btn-elegant w-full" data-cursor="pointer">
                              <span>View Details</span>
                            </button>
                          </MagneticButton>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </section>

            <Footer />
          </div>
        </main>
      </SmoothScroll>

      <ScrollAnimations />
      <RevealObserver />
    </>
  );
}